import { useCallback, useMemo } from "react"
import { toDcfKana } from "shared/text"
import { CopyIcon } from "shared/icons"
import { enumJapaneeseReplacePattern } from "constants/japaneeseReplacePatterns"
import { Divider, IconButton, Stack, Typography } from "@mui/material"
import { SearchWordField } from "components/controls/searchWordField"
import { GridFilter } from "../../types"

type SentenseFilterProps = {
	field: string
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	values: any[] | null
	filter: GridFilter | undefined
	filters: GridFilter[]
	setFilters: (filters: GridFilter[]) => void
}
export const SentenseFilter = ({
	field,
	values,
	filter,
	filters,
	setFilters
}: SentenseFilterProps) => {
	const onChangeString = useCallback(
		(value: string) => {
			if (!field) return

			const trimedValue = value.trim().replaceAll('　', ' ')
			const textPatterns = (trimedValue === '') ? [] : (
				enumJapaneeseReplacePattern(trimedValue).concat(trimedValue).flatMap(value => {
					const kana = toDcfKana(value)
					if (kana === value) return value
					return [value, kana]
				})
			)
			if (filter) {
				if (textPatterns.length === 0) {
					// remove filter
					setFilters(filters.filter(f => f.field !== filter.field).slice())
				} else {
					// replace filter
					setFilters(filters.map(f => f.field === filter.field ? {...filter, label: trimedValue, textPatterns } : f).slice())
				}
			} else {
				// new filter
				setFilters(filters.concat({ field, label: trimedValue, textPatterns }).slice())
			}
		},
		[field, filters, setFilters, filter]
	)
	const words = useMemo(
		() => {
			const words = values?.flatMap(value => {
				if (typeof value !== 'string') return []
				return value.split(/[\t,\s]/i).flatMap(word => {
					const trimed = word.trim()
					if (trimed === '' || (trimed.startsWith('(') && trimed.endsWith(')'))) return []
					return trimed
				})
			}) ?? []
			const set: { [word: string]: { word: string, count: number } } = {}
			words.forEach(word => {
				const e = set[word]
				if (e) {
					e.count++
				} else {
					set[word] = { word, count: 1 }
				}
			})
			const list = Object.values(set)
			list.sort((a, b) => b.count - a.count)
			return list.map(e => e.word)
		},
		[values]
	)
	return (
		<Stack direction="column" padding={1} spacing={1} sx={{ overflowY: "auto" }}>
			<SearchWordField
				searchWord={filter?.label ?? ''}
				onChangeSearchWord={onChangeString}
			/>
			<Divider/>
			<Typography variant="caption" color="text.secondary">フォーカスセルの内容からコピー...</Typography>
			{words.map((line, i) => (
				<Stack key={`line-${i}`} direction="row" spacing={1} alignItems="center">
					<IconButton size="small" onClick={() => {
						onChangeString(line.trim())
					}}>
						<CopyIcon fontSize="small"/>
					</IconButton>
					<Typography variant="body2">{line.trim()}</Typography>
				</Stack>
			))}
		</Stack>
	)
}

